import React, { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { GridCell } from "@/components/list/Schedule/components/GridCell";
import { HISTORY_ALERT_COLOR, SafetyInstructionNumbers } from "@/components/list/Schedule/constants";
import { ScheduleField, ScheduleItem } from "@/components/list/Schedule/types";
import storageManager from "@/lib/storageManager";

type Props = {
  field: ScheduleField;
  index: number;
  item: ScheduleItem;
  editableSekou: boolean;
  editableSeiyu: boolean;
  canContinuousUpdateSekou: boolean;
  canContinuousUpdateSeiyu: boolean;
  isContinuousUpdateMode: boolean;
  continuousUpdateStatus: number;
  loadingItemId: number;
  loadingTarget: string;
  setTooltip: (text: string) => void;
  editSelectItem: (label: string, target: string, value?: string, loadingTarget?: string) => void;
  textEditor: {
    id: number;
    target: string;
    enter: (params: { item: ScheduleItem; target: string }) => void;
    submit: (value: string, e: HTMLTextAreaElement) => void;
  };
  onClickHistory?: (item: ScheduleItem, targetColumn: string, targetName: string) => void;
};

export const ConfigurablePlanListCell = ({
  field,
  index,
  item,
  editableSekou,
  editableSeiyu,
  canContinuousUpdateSekou,
  canContinuousUpdateSeiyu,
  isContinuousUpdateMode,
  continuousUpdateStatus,
  loadingItemId,
  loadingTarget,
  setTooltip,
  editSelectItem,
  textEditor,
  onClickHistory,
}: Props) => {
  const { t } = useTranslation();
  const roles = item.roles;
  const fieldKey = field.field_key;
  const label = useMemo(() => {
    const locale = storageManager.getLocale();
    const customLabel = locale === "ja" ? field.field_label_jp : field.field_label_en;
    switch (fieldKey) {
      case "field1":
        return customLabel || t("hazard") + t("rank");
      case "field2":
        return customLabel || t("attendance");
      case "field3":
        return customLabel || t("construction_attend") + t("company_title");
      case "field4":
        return customLabel || `${t("safety_instruction_detail")} : ${t("hazard_source")}`;
      case "field5":
        return customLabel || `${t("safety_instruction_detail")} : ${t("hazard_reduction_activity")}`;
      case "field_t1":
        return customLabel || t("number_of_workers");
      case "field_t2":
        return customLabel || `${t("fire")} : ${t("open_flame")}`;
      case "field_t3":
        return customLabel || `${t("fire")} : ${t("usual")}${t("fire")}`;
      case "field_t4":
        return customLabel || `${t("fire")} : ${t("use_flame")}`;
      case "field_t5":
        return customLabel || t("pipe") + t("cutting");
      case "field_t6":
        return customLabel || t("lack_of_oxygen");
      case "field_t7":
        return customLabel || t("heavy_machine");
      case "field_t8":
        return customLabel || t("safety") + t("instruction");
      case "field_t9":
        return customLabel || t("overtime_schedule");
      case "approve1":
        return customLabel || t("construction") + t("supervising_section");
      case "approve2":
        return customLabel || t("PL");
      case "approve3":
        return customLabel || t("TL");
      case "approve4":
        return customLabel || t("facility") + t("supervising_section");
      default:
        return "";
    }
  }, [field.field_label_en, field.field_label_jp, fieldKey, t]);
  const historyAlertColor = item.history_alert?.update?.includes(fieldKey)
    ? HISTORY_ALERT_COLOR.UPDATE
    : item.history_alert?.approve?.includes(fieldKey)
    ? HISTORY_ALERT_COLOR.APPROVE
    : undefined;
  const handleClickHistory = () => {
    onClickHistory?.(item, fieldKey, label);
  };
  const cellProps = { historyAlertColor, onClickHistory: handleClickHistory };

  // 危険度ランク
  if (fieldKey === "field1") {
    return (
      <GridCell
        {...cellProps}
        className="col13"
        label={item.field1_name}
        editable={roles.field1 && editableSekou && !isContinuousUpdateMode}
        onClick={() => editSelectItem(label, "field1")}
        data-test-id={`schedule-${index + 1}-field1`}
      />
    );
  }
  // 立会
  if (fieldKey === "field2") {
    return (
      <GridCell
        {...cellProps}
        className="col14"
        label={item.field2_name}
        editable={roles.field2 && editableSeiyu && item.field2 !== continuousUpdateStatus}
        disableContinuousUpdate={!canContinuousUpdateSekou}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "field2" === loadingTarget}
        onClick={() => editSelectItem(label, "field2")}
        data-test-id={`schedule-${index + 1}-field2`}
      />
    );
  }
  // 施工会社
  if (fieldKey === "field3") {
    return (
      <GridCell
        {...cellProps}
        className="col5"
        align="left"
        label={item.field3}
        editable={roles.field3 && editableSekou && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field3`}
      />
    );
  }
  // 個別安全確保事項：危険源
  if (fieldKey === "field4") {
    return (
      <GridCell
        {...cellProps}
        className="col16"
        align="left"
        label={item.field4}
        editable={roles.field4 && editableSekou && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field4`}
      />
    );
  }
  // 個別安全確保事項：低減策
  if (fieldKey === "field5") {
    return (
      <GridCell
        {...cellProps}
        className="col17"
        align="left"
        label={item.field5}
        editable={roles.field5 && editableSekou && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field5`}
      />
    );
  }
  // 人数
  if (fieldKey === "field_t1") {
    return (
      <GridCell
        {...cellProps}
        className="col6"
        label={item.field_t1?.toString()}
        editable={roles.field_t1 && editableSekou && !isContinuousUpdateMode}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field-t1`}
      />
    );
  }
  // 火気：裸火
  if (fieldKey === "field_t2") {
    return (
      <GridCell
        {...cellProps}
        className="col7"
        label={item.field_t2_name}
        editable={roles.field_t2 && editableSekou && item.field_t2 !== continuousUpdateStatus}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "field_t2" === loadingTarget}
        disableContinuousUpdate={!canContinuousUpdateSekou}
        onClick={() => editSelectItem(label, "field_t2")}
        data-test-id={`schedule-${index + 1}-field-t2`}
      />
    );
  }
  // 火気：一般火気
  if (fieldKey === "field_t3") {
    return (
      <GridCell
        {...cellProps}
        className="col8"
        label={item.field_t3_name}
        editable={roles.field_t3 && editableSekou && item.field_t3 !== continuousUpdateStatus}
        disableContinuousUpdate={!canContinuousUpdateSekou}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "field_t3" === loadingTarget}
        onClick={() => editSelectItem(label, "field_t3")}
        data-test-id={`schedule-${index + 1}-field-t3`}
      />
    );
  }
  // 火気：使用火気
  if (fieldKey === "field_t4") {
    return (
      <GridCell
        {...cellProps}
        className="col9"
        align="left"
        label={item.field_t4}
        editable={roles.field_t4 && editableSekou && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field-t4`}
      />
    );
  }
  // 配管切断
  if (fieldKey === "field_t5") {
    return (
      <GridCell
        {...cellProps}
        className="col10"
        label={item.field_t5_name}
        editable={roles.field_t5 && editableSekou && item.field_t5 !== continuousUpdateStatus}
        disableContinuousUpdate={!canContinuousUpdateSekou}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "field_t5" === loadingTarget}
        onClick={() => editSelectItem(label, "field_t5")}
        data-test-id={`schedule-${index + 1}-field-t5`}
      />
    );
  }
  // 酸欠
  if (fieldKey === "field_t6") {
    return (
      <GridCell
        {...cellProps}
        className="col12"
        label={item.field_t6_name}
        editable={roles.field_t6 && editableSekou && item.field_t6 !== continuousUpdateStatus}
        disableContinuousUpdate={!canContinuousUpdateSekou}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "field_t6" === loadingTarget}
        onClick={() => editSelectItem(label, "field_t6")}
        data-test-id={`schedule-${index + 1}-field-t6`}
      />
    );
  }
  // 重機
  if (fieldKey === "field_t7") {
    return (
      <GridCell
        {...cellProps}
        className="col11"
        align="left"
        label={item.field_t7}
        editable={roles.field_t7 && editableSekou && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field-t7`}
      />
    );
  }
  // 安全指示
  if (fieldKey === "field_t8") {
    return (
      <GridCell
        {...cellProps}
        className="col15 break-word"
        label={item.field_t8}
        editable={roles.field_t8 && editableSekou && !isContinuousUpdateMode}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field-t8`}
      >
        {item.field_t8?.split(",").map((v) => SafetyInstructionNumbers[v])}
      </GridCell>
    );
  }
  // 残業予定時間
  if (fieldKey === "field_t9") {
    return (
      <GridCell
        {...cellProps}
        className="col6"
        label={Number.isFinite(item.field_t9) ? item.field_t9.toString() : ""}
        editable={roles.field_t9 && editableSekou && !isContinuousUpdateMode}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-field-t9`}
      />
    );
  }
  // 工事主管課
  if (fieldKey === "approve1") {
    return (
      <GridCell
        {...cellProps}
        className="col18"
        editable={roles.approve1 && editableSeiyu}
        disableContinuousUpdate={!canContinuousUpdateSeiyu}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "approve1" === loadingTarget}
        onClick={() => editSelectItem(label, "approve1_status", item.approve1_status?.toString(), "approve1")}
        data-test-id={`schedule-${index + 1}-approve1`}
      >
        {item.approve1_status_name}
        <br />
        {item.approve1_status_name ? item.approve1_user_name : ""}
      </GridCell>
    );
  }
  // PL
  if (fieldKey === "approve2") {
    return (
      <GridCell
        {...cellProps}
        className="col19"
        editable={roles.approve2 && editableSeiyu && item.field1 !== 0} // 危険度ランクが設定されていれば更新可能
        disableContinuousUpdate={!canContinuousUpdateSeiyu}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "approve2" === loadingTarget}
        onClick={() => editSelectItem(label, "approve2_status", item.approve2_status?.toString(), "approve2")}
        data-test-id={`schedule-${index + 1}-approve2`}
      >
        {item.approve2_status_name}
        <br />
        {item.approve2_status_name ? item.approve2_user_name : ""}
      </GridCell>
    );
  }
  // TL
  if (fieldKey === "approve3") {
    return (
      <GridCell
        {...cellProps}
        className="col20"
        editable={roles.approve3 && editableSeiyu && item.field1 !== 0} // 危険度ランクが設定されていれば更新可能
        disableContinuousUpdate={!canContinuousUpdateSeiyu}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "approve3" === loadingTarget}
        onClick={() => editSelectItem(label, "approve3_status", item.approve3_status?.toString(), "approve3")}
        data-test-id={`schedule-${index + 1}-approve3`}
      >
        {item.approve3_status_name}
        <br />
        {item.approve3_status_name ? item.approve3_user_name : ""}
      </GridCell>
    );
  }
  // 設備主管課
  if (fieldKey === "approve4") {
    return (
      <GridCell
        {...cellProps}
        className="col22"
        editable={roles.approve4 && editableSeiyu}
        disableContinuousUpdate={!canContinuousUpdateSeiyu}
        continuousUpdateStatus={continuousUpdateStatus}
        isLoading={isContinuousUpdateMode && item.schedule_task_id === loadingItemId && "approve4" === loadingTarget}
        onClick={() => editSelectItem(label, "approve4_status", item.approve4_status?.toString(), "approve4")}
        data-test-id={`schedule-${index + 1}-approve4`}
      >
        {item.approve4_status_name}
        <br />
        {item.approve4_status_name ? item.approve4_user_name : ""}
      </GridCell>
    );
  }
  // 工事主管課コメント
  if (fieldKey === "note1") {
    return (
      <GridCell
        {...cellProps}
        className="col21"
        align="left"
        label={item.note1 || ""}
        editable={roles.note1 && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-note1`}
      />
    );
  }
  // 設備主管課コメント
  if (fieldKey === "note2") {
    return (
      <GridCell
        {...cellProps}
        className="col23"
        align="left"
        label={item.note2 || ""}
        editable={roles.note2 && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-note2`}
      />
    );
  }
  // 元請コメント
  if (fieldKey === "note3") {
    return (
      <GridCell
        {...cellProps}
        className="w-[120px] min-w-[120px]"
        align="left"
        label={item.note3 || ""}
        editable={roles.note3 && !isContinuousUpdateMode}
        setTooltip={setTooltip}
        editing={textEditor.id && textEditor.id === item.schedule_task_id && textEditor.target === fieldKey}
        onEnter={() => textEditor.enter({ item, target: fieldKey })}
        onBlur={(value, e) => textEditor.submit(value, e)}
        data-test-id={`schedule-${index + 1}-note3`}
      />
    );
  }
  return <></>;
};
